// @ts-nocheck
import React from 'react';
import { Info } from 'lucide-react';
import { clientSurfaceCardClass } from '../../../utils/clientChromeClasses';

/* ─── SHARED COMPONENTS (client workflow forms) ─── */
export const PageHeader = ({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  /** Small label above the title (e.g. section context). */
  eyebrow?: React.ReactNode;
  /** Omit when the shell already shows the page title (e.g. workflow overview summary row). */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: unknown;
}) => (
  <div className="flex w-full flex-col gap-3 border-b border-gray-100 pb-4 sm:gap-4 sm:pb-5 md:flex-row md:items-start md:justify-between md:gap-6">
    <div className="min-w-0 flex-1">
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800/90 mb-1.5">{eyebrow}</p>
      ) : null}
      {title != null && title !== '' ? (
        <h1 className="text-lg font-medium tracking-tight text-gray-900 sm:text-xl">{title}</h1>
      ) : null}
      {subtitle ? (
        <p className={`text-xs leading-snug text-gray-600 ${title != null && title !== '' ? 'mt-1.5' : ''}`}>
          {subtitle}
        </p>
      ) : null}
    </div>
    {action != null && action !== false ? (
      <div className="flex w-full shrink-0 justify-end self-start md:ml-auto md:w-auto">{action}</div>
    ) : null}
  </div>
);

/** Intro under the shell title (Analyses, Run History, Archived, Settings) — matches workflow subtitle styling. */
export const ClientPageLede = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-2 max-w-2xl border-b border-gray-100 pb-3">
    <div className="space-y-2 text-sm leading-snug text-gray-600 sm:text-base">{children}</div>
  </div>
);

export const Card = ({
  children,
  className = '',
  padding = true,
}: {
  children?: unknown;
  className?: string;
  padding?: boolean;
  key?: string | number;
}) => <div className={`${clientSurfaceCardClass} ${padding ? 'p-5' : ''} ${className}`}>{children}</div>;

export const Select = ({ label, children, ...props }: { label?: string; children?: unknown; [k: string]: unknown }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-700 mb-2">{label}</label>}
    <select
      {...props}
      className="w-full h-10 px-3 text-xs bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all appearance-none"
    >
      {children}
    </select>
  </div>
);

/** Compact chip (headers, cards). `table` matches client Analyses / firm workflow status column padding. */
export const Badge = ({
  children,
  variant = 'default',
  size = 'default',
  className = '',
}: {
  children?: unknown;
  variant?: string;
  size?: 'default' | 'table';
  className?: string;
}) => {
  const compactStyles: Record<string, string> = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border border-amber-200',
    danger: 'bg-red-50 text-red-700 border border-red-200',
    info: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
    /** Equity type chips — distinct hues (Parachute → Equity Awards). */
    equityOption: 'bg-sky-50 text-sky-800 border border-sky-200',
    equityRsu: 'bg-violet-50 text-violet-800 border border-violet-200',
    equityRsa: 'bg-orange-50 text-orange-900 border border-orange-200',
  };
  const tableStyles: Record<string, string> = {
    default: 'bg-gray-100/80 border-gray-200/60 text-gray-600',
    success: 'bg-emerald-100/80 border-emerald-200/60 text-emerald-800',
    warning: 'bg-amber-100/80 border-amber-200/60 text-amber-800',
    danger: 'bg-red-100/80 border-red-200/60 text-red-800',
    info: 'bg-emerald-100/80 border-emerald-200/60 text-emerald-800',
    equityOption: 'bg-sky-100/80 border-sky-200/60 text-sky-900',
    equityRsu: 'bg-violet-100/80 border-violet-200/60 text-violet-900',
    equityRsa: 'bg-orange-100/80 border-orange-200/60 text-orange-950',
  };
  if (size === 'table') {
    const v = tableStyles[variant] || tableStyles.default;
    return (
      <span
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm ${v} ${className}`.trim()}
      >
        {children}
      </span>
    );
  }
  const v = compactStyles[variant] || compactStyles.default;
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${v} ${className}`.trim()}>
      {children}
    </span>
  );
};

export const Input = ({
  label,
  note,
  className,
  ...props
}: {
  label?: string;
  note?: string;
  className?: string;
  [k: string]: unknown;
}) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-700 mb-2">{label}</label>}
    <input
      {...props}
      className={[
        'w-full h-10 px-3 text-xs bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all placeholder:text-gray-400',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
    {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
  </div>
);

export const Button = ({ children, variant = 'primary', size = 'default', className, type = 'button', ...props }) => {
  const base =
    'inline-flex items-center justify-center font-medium rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'btn-primary text-white focus:ring-blue-400 shadow-sm',
    secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
  };
  const sizes = { sm: 'text-xs px-3 py-1.5', default: 'text-xs px-4 py-2.5', lg: 'text-xs px-5 py-2.5' };
  const mergedClassName = [
    base,
    variants[variant],
    sizes[size],
    'disabled:opacity-40 disabled:cursor-not-allowed',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={mergedClassName} {...props}>
      {children}
    </button>
  );
};

/** Centered destructive-confirm overlay (remove rows, awards, individuals, etc.). */
export const ConfirmDialog = ({
  open,
  title,
  description,
  children,
  confirmLabel = 'Remove',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  zClassName = 'z-50',
}) => {
  const titleId = React.useId();
  if (!open) return null;
  return (
    <div className={`fixed inset-0 ${zClassName} flex items-center justify-center p-4`}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative mx-4 w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl"
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 id={titleId} className="text-xs font-medium text-gray-900">
            {title}
          </h3>
          {description ? <p className="mt-2 text-xs leading-relaxed text-gray-600">{description}</p> : null}
          {children ? <div className="mt-3">{children}</div> : null}
        </div>
        <div className="flex justify-end gap-2 rounded-b-lg border-t border-gray-200 bg-gray-50 px-5 py-4">
          <Button variant="secondary" size="sm" type="button" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="danger" size="sm" type="button" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Shared 36×36 table row icon actions (Equity Awards, Buyer Equity, Other Payments, Disqualified Individuals, Vote Room).
 */
const WORKFLOW_TABLE_ICON_BTN_BASE =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-offset-1';

export const workflowTableIconButtonEditClass = `${WORKFLOW_TABLE_ICON_BTN_BASE} hover:bg-emerald-50 hover:text-emerald-700 focus-visible:ring-2 focus-visible:ring-emerald-500/45`;

export const workflowTableIconButtonViewClass = `${WORKFLOW_TABLE_ICON_BTN_BASE} hover:bg-blue-50 hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500/35`;

export const workflowTableIconButtonDangerClass = `${WORKFLOW_TABLE_ICON_BTN_BASE} hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-red-500/35`;

/** Table `<th>` styling shared by workflow data tables (documents, Vote Room, etc.). */
export const workflowTableHeadClass =
  'px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

/** Primary action button (slate) used in Vote Room wizard, error modals, and similar chrome. */
export const workflowPrimaryButtonClass =
  'inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors';

export const workflowBackButtonClass =
  'inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors';

export const workflowToolbarButtonClass =
  'inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors';

export const workflowToolbarPrimaryButtonClass =
  'inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors';

export const StatCard = ({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: unknown;
  sub?: unknown;
  icon?: (props: { size?: number; className?: string }) => unknown;
}) => (
  <Card className="relative overflow-hidden">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-lg font-medium text-gray-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
      {Icon && (
        <div className="w-9 h-9 rounded-md bg-gray-50 border border-gray-100 flex items-center justify-center">
          <Icon size={16} className="text-gray-400" />
        </div>
      )}
    </div>
  </Card>
);

export const Th = ({ children, className = '' }: { children?: unknown; className?: string; key?: number }) => (
  <th className={`px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
    {children}
  </th>
);

export const Td = ({
  children,
  className = '',
  highlight,
}: {
  children?: unknown;
  className?: string;
  highlight?: boolean;
  key?: string | number;
}) => <td className={`px-3 py-3 text-xs ${highlight ? 'font-medium' : 'text-gray-700'} ${className}`}>{children}</td>;

export const InfoBanner = ({ variant = 'info', children, showIcon = false }) => {
  const styles = {
    info: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div
      className={`text-xs border rounded-md px-4 py-3.5 ${styles[variant]} ${showIcon ? 'flex gap-3 items-start' : ''}`}
    >
      {showIcon && (
        <span className="flex-shrink-0 text-current opacity-90" aria-hidden>
          <Info size={20} strokeWidth={2} className="mt-0.5" />
        </span>
      )}
      {showIcon ? <div className="min-w-0 flex-1">{children}</div> : children}
    </div>
  );
};

export { WorkflowDateField } from './WorkflowDateField';
